import { useRef, useState } from "react";
import {
  Download, Upload, X, ChevronDown, Loader2, AlertCircle,
  CheckCircle2, FileSpreadsheet, ArrowRight, Info,
} from "lucide-react";
import api, { formatErr, fileUrl, getToken } from "../lib/apiClient";

/**
 * ExportImportBar — dropdown to trigger CSV/XLSX exports, and (customers only) open
 * an import modal that runs a dry-run preview before committing.
 *
 * Props:
 *   entity: "customers" | "invoices"
 *   onImportComplete: () => void  — called after a successful commit so the parent list refreshes
 */
export function ExportImportBar({ entity, onImportComplete }) {
  const [openMenu, setOpenMenu] = useState(false);
  const [openTemplateMenu, setOpenTemplateMenu] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const download = (fmt) => {
    // Auth-aware: use fetch to preserve the Bearer header, then blob-save the response.
    const url = fileUrl(`/exports/${entity}.${fmt}`);
    const token = getToken();
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const stamp = new Date().toISOString().slice(0, 10);
        a.href = href;
        a.download = `${entity}-${stamp}.${fmt}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
      })
      .catch((e) => alert(`Export failed: ${e.message}`))
      .finally(() => setOpenMenu(false));
  };

  // Blank import template with headers + a sample row. User overwrites the sample
  // and re-imports through the existing dry-run → commit modal below.
  const downloadTemplate = (fmt) => {
    const url = fileUrl(`/imports/customers/template.${fmt}`);
    const token = getToken();
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = `customer-import-template.${fmt}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
      })
      .catch((e) => alert(`Template download failed: ${e.message}`));
  };

  return (
    <>
      <div className="relative inline-block" data-testid={`export-import-bar-${entity}`}>
        <button
          onClick={() => setOpenMenu((v) => !v)}
          data-testid={`export-menu-btn-${entity}`}
          className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 font-medium text-slate-700 dark:text-slate-300"
        >
          <Download size={14} /> Export <ChevronDown size={14} />
        </button>
        {openMenu && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpenMenu(false)} />
            <div className="absolute right-0 mt-1 w-48 z-40 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md shadow-lg py-1">
              <button onClick={() => download("csv")} data-testid={`export-${entity}-csv`}
                className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2">
                <FileSpreadsheet size={14} className="text-slate-400" /> Download .csv
              </button>
              <button onClick={() => download("xlsx")} data-testid={`export-${entity}-xlsx`}
                className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2">
                <FileSpreadsheet size={14} className="text-emerald-600" /> Download .xlsx
              </button>
            </div>
          </>
        )}
      </div>
      {entity === "customers" && (
        <>
          <button
            onClick={() => setShowImport(true)}
            data-testid="import-customers-btn"
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 font-medium text-slate-700 dark:text-slate-300"
          >
            <Upload size={14} /> Import
          </button>
          <div className="relative inline-block">
            <button
              onClick={() => setOpenTemplateMenu((v) => !v)}
              data-testid="download-template-btn"
              className="px-3 py-2 text-sm border border-dashed border-slate-300 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 font-medium text-slate-700 dark:text-slate-300"
              title="Download a blank import template — fill in your customers, then re-import"
            >
              <FileSpreadsheet size={14} /> Template <ChevronDown size={14} />
            </button>
            {openTemplateMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setOpenTemplateMenu(false)} />
                <div className="absolute right-0 mt-1 w-56 z-40 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md shadow-lg py-1">
                  <button
                    onClick={() => { downloadTemplate("csv"); setOpenTemplateMenu(false); }}
                    data-testid="download-template-csv"
                    className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2"
                  >
                    <FileSpreadsheet size={14} className="text-slate-400" /> Blank template .csv
                  </button>
                  <button
                    onClick={() => { downloadTemplate("xlsx"); setOpenTemplateMenu(false); }}
                    data-testid="download-template-xlsx"
                    className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2"
                  >
                    <FileSpreadsheet size={14} className="text-emerald-600" /> Blank template .xlsx
                  </button>
                  <div className="border-t border-slate-100 dark:border-slate-800 mt-1 pt-1">
                    <p className="px-4 py-2 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                      Includes headers, a sample row (delete before importing) and an Instructions sheet in the .xlsx.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
      {showImport && (
        <ImportCustomersModal
          onClose={() => setShowImport(false)}
          onComplete={() => { setShowImport(false); onImportComplete?.(); }}
        />
      )}
    </>
  );
}

/* --------------- Import modal (dry-run then commit) --------------- */

function ImportCustomersModal({ onClose, onComplete }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("skip");
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");
  const [committed, setCommitted] = useState(false);
  const fileInputRef = useRef(null);

  const upload = async (isDryRun) => {
    if (!file) return;
    setRunning(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/imports/customers?dry_run=${isDryRun}&mode=${mode}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (isDryRun) setPreview(data);
      else { setCommitted(true); setPreview(data); }
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setRunning(false);
    }
  };

  const stats = preview?.stats;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="import-modal">
      <div className="bg-white dark:bg-slate-900 rounded-md max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
          <h3 className="font-display font-bold text-lg text-slate-900 dark:text-slate-100">Import customers</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" data-testid="import-close"><X size={18} /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          {!committed && (
            <div className="bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-900 rounded p-3 flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300">
              <Info size={16} className="mt-0.5 text-sky-600 shrink-0" />
              <div>
                <div className="font-medium">Import happens in two steps</div>
                <ol className="mt-1 list-decimal ml-4 space-y-0.5 text-xs">
                  <li>Upload → <b>Preview</b>: shows what would happen. Nothing written yet.</li>
                  <li>Confirm → <b>Commit</b>: writes new/updated customers.</li>
                </ol>
                <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Matching is by email only (case-insensitive). Missing/no-match rows create new. Every row needs at least one of: <code>First name</code>, <code>Last name</code>, or <code>Display name</code>.
                </div>
                <div className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                  Don't have a file yet? Close this modal and click <b>Template</b> in the toolbar for a blank .csv/.xlsx with the exact expected columns.
                </div>
              </div>
            </div>
          )}

          {!committed && (
            <>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">File (.csv or .xlsx)</label>
                <div className="mt-2 flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setErr(""); }}
                    data-testid="import-file-input"
                    className="flex-1 text-sm file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-slate-100 dark:file:bg-slate-800 file:rounded file:text-sm file:font-medium file:cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">If a customer already exists (by email)</label>
                <div className="mt-2 space-y-2">
                  {[
                    { v: "skip", label: "Skip", sub: "Leave the existing customer untouched (recommended)" },
                    { v: "update", label: "Update", sub: "Overwrite existing fields with values from the file. Empty cells preserve current data." },
                    { v: "create-only", label: "Reject duplicates", sub: "Fail any row whose email already exists — only strict-new customers get created" },
                  ].map((opt) => (
                    <label key={opt.v} className="flex items-start gap-2.5 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                      <input
                        type="radio"
                        name="import-mode"
                        value={opt.v}
                        checked={mode === opt.v}
                        onChange={() => { setMode(opt.v); setPreview(null); }}
                        data-testid={`import-mode-${opt.v}`}
                        className="mt-0.5 w-4 h-4 accent-[#0F4C81]"
                      />
                      <div>
                        <div className="font-medium">{opt.label}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{opt.sub}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {err && (
                <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 text-sm px-3 py-2 rounded" data-testid="import-error">
                  {err}
                </div>
              )}
            </>
          )}

          {preview && (
            <div className="border border-slate-200 dark:border-slate-800 rounded" data-testid="import-preview">
              <div className={`px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2 ${committed ? "bg-emerald-50 dark:bg-emerald-950/40" : "bg-amber-50 dark:bg-amber-950/40"}`}>
                {committed ? <CheckCircle2 size={16} className="text-emerald-600" /> : <AlertCircle size={16} className="text-amber-600" />}
                <div className="font-medium text-sm">{committed ? "Import complete" : "Preview — nothing has been written yet"}</div>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
                  <StatBox label="Total rows" value={preview.total_rows} />
                  <StatBox label="New" value={stats.new} highlight={stats.new > 0 ? "emerald" : null} />
                  <StatBox label="Updated" value={stats.updated} highlight={stats.updated > 0 ? "sky" : null} />
                  <StatBox label="Skipped" value={stats.skipped_matched} />
                  <StatBox label="Rejected" value={stats.rejected} highlight={stats.rejected > 0 ? "red" : null} />
                </div>

                {preview.ignored_computed_headers?.length > 0 && (
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    <span className="font-medium">Computed columns ignored:</span> {preview.ignored_computed_headers.join(", ")}
                  </div>
                )}
                {preview.unknown_headers?.length > 0 && (
                  <div className="text-xs text-amber-700 dark:text-amber-400">
                    <span className="font-medium">Unknown columns (ignored):</span> {preview.unknown_headers.join(", ")}
                  </div>
                )}

                {/* Action rows */}
                <div className="max-h-52 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded" data-testid="import-actions-list">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Row</th>
                        <th className="px-2 py-1.5 text-left">Action</th>
                        <th className="px-2 py-1.5 text-left">Name</th>
                        <th className="px-2 py-1.5 text-left">Email</th>
                        <th className="px-2 py-1.5 text-left">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.actions.map((a) => (
                        <tr key={`${a.row}-${a.action}`} className="border-b border-slate-100 dark:border-slate-800 last:border-b-0">
                          <td className="px-2 py-1.5 tabular text-slate-500">{a.row}</td>
                          <td className="px-2 py-1.5"><ActionBadge action={a.action} /></td>
                          <td className="px-2 py-1.5">{a.name || <span className="text-slate-400 italic">—</span>}</td>
                          <td className="px-2 py-1.5">{a.email || <span className="text-slate-400 italic">—</span>}</td>
                          <td className="px-2 py-1.5 text-slate-500 dark:text-slate-400">{a.reason || (a.fields ? `Updating: ${a.fields.join(", ")}` : "")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-3">
          {committed ? (
            <button onClick={onComplete} data-testid="import-done-btn"
              className="px-4 py-2 text-sm bg-[#0F4C81] hover:bg-[#0A365C] text-white rounded font-medium">Done</button>
          ) : preview ? (
            <>
              <button onClick={() => setPreview(null)} className="px-4 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800">Back</button>
              <button
                onClick={() => upload(false)}
                disabled={running || stats.new + stats.updated === 0}
                data-testid="import-commit-btn"
                className="px-4 py-2 text-sm bg-[#0F4C81] hover:bg-[#0A365C] text-white rounded font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {running ? <><Loader2 size={14} className="animate-spin" /> Committing…</> :
                  <>Commit changes <ArrowRight size={14} /></>}
              </button>
            </>
          ) : (
            <button
              onClick={() => upload(true)}
              disabled={!file || running}
              data-testid="import-preview-btn"
              className="px-4 py-2 text-sm bg-[#0F4C81] hover:bg-[#0A365C] text-white rounded font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {running ? <><Loader2 size={14} className="animate-spin" /> Analyzing…</> :
                <>Preview import <ArrowRight size={14} /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, highlight }) {
  const colors = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    sky: "text-sky-600 dark:text-sky-400",
    red: "text-red-600 dark:text-red-400",
  };
  return (
    <div className="border border-slate-200 dark:border-slate-800 rounded p-2.5">
      <div className={`font-display text-xl font-bold tabular ${highlight ? colors[highlight] : "text-slate-900 dark:text-slate-100"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium mt-0.5">{label}</div>
    </div>
  );
}

function ActionBadge({ action }) {
  const styles = {
    create: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300",
    update: "bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-300",
    skip: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
    reject: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300",
  };
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${styles[action] || ""}`}>{action}</span>;
}
